import React, { useState, useEffect } from 'react';
import { 
    PencilSquareIcon, TrashIcon, CheckIcon, XMarkIcon, MagnifyingGlassIcon,
    ChatBubbleLeftIcon, MicrophoneIcon, PhotoIcon, DocumentTextIcon, FaceSmileIcon,
    ArrowUpIcon, ArrowDownIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { LeadsAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface CatalogItem {
  item_id: string;
  type: string;
  mime_type: string;
  delay_before_send_ms: number;
  caption?: string;
  text_preview?: string;
  ref_name?: string;
}

interface CatalogBlock {
  funnel_id: string;
  description: string;
  items: CatalogItem[];
  fase?: string;
}

const FASES = [
  { id: 'fase1',       label: 'Fase 1',       color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  { id: 'fase2',       label: 'Fase 2',       color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
  { id: 'fase3',       label: 'Fase 3',       color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  { id: 'fase4',       label: 'Fase 4',       color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' },
  { id: 'nova_aluna',  label: 'Nova Aluna',   color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  { id: 'remarketing', label: 'Remarketing',  color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' },
  { id: 'followup',    label: 'Follow Up',    color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300' },
];

function FaseBadge({ faseId, className = '' }: { faseId?: string; className?: string }) {
  if (!faseId) return <span className={`px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 ${className}`}>Sem fase</span>;
  const f = FASES.find(f => f.id === faseId);
  if (!f) return <span className={`px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500 ${className}`}>{faseId}</span>;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${f.color} ${className}`}>{f.label}</span>;
}

const CatalogEditor = () => {
  const [blocks, setBlocks] = useState<Record<string, CatalogBlock>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [editingBlock, setEditingBlock] = useState<string | null>(null);
  const [editedItems, setEditedItems] = useState<CatalogItem[]>([]);
  
  // New Block State
  const [showNewBlockModal, setShowNewBlockModal] = useState(false);
  const [newBlockName, setNewBlockName] = useState('');
  const [newBlockDesc, setNewBlockDesc] = useState('');
  const [newBlockFase, setNewBlockFase] = useState('');

  // Fase edit
  const [editedFase, setEditedFase] = useState<string>('');

  // Search + Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [faseFilter, setFaseFilter] = useState('');

  useEffect(() => {
    loadCatalog();
  }, []);

  const loadCatalog = async () => {
    try {
      setIsLoading(true);
      const data = await LeadsAPI.getCatalog();
      setBlocks(data);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao carregar catálogo');
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (blockName: string, items: CatalogItem[], fase?: string) => {
    setEditingBlock(blockName);
    setEditedItems(JSON.parse(JSON.stringify(items))); // Deep copy
    setEditedFase(fase || '');
  };

  const cancelEdit = () => {
    setEditingBlock(null);
    setEditedItems([]);
    setEditedFase('');
  };

  const saveEdit = async (blockName: string) => {
    try {
      const newBlocks = { ...blocks };
      newBlocks[blockName] = {
        ...newBlocks[blockName],
        items: editedItems,
        fase: editedFase || undefined,
      };
      await LeadsAPI.saveCatalog(newBlocks);
      setBlocks(newBlocks);
      setEditingBlock(null);
      setEditedFase('');
      toast.success('Bloco atualizado com sucesso!');
    } catch (error: any) {
      toast.error('Erro ao salvar alterações');
    }
  };

  const addItemToBlock = () => {
    setEditedItems([
      ...editedItems,
      {
        item_id: crypto.randomUUID(),
        type: 'message',
        mime_type: 'text/plain',
        delay_before_send_ms: 2000,
        text_preview: '',
        caption: ''
      }
    ]);
  };

  const removeItemFromBlock = (index: number) => {
    const newItems = [...editedItems];
    newItems.splice(index, 1);
    setEditedItems(newItems);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newItems = [...editedItems];
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= newItems.length) return;
    [newItems[index], newItems[swapWith]] = [newItems[swapWith], newItems[index]];
    setEditedItems(newItems);
  };

  const getResources = (type: string) => {
      const unique = new Set<string>();
      Object.values(blocks).forEach(block => {
          block.items.forEach(item => {
              if (item.type === type) {
                  if (type === 'message' && item.text_preview) unique.add(item.text_preview);
                  else if (item.ref_name) unique.add(item.ref_name);
              }
          });
      });
      return Array.from(unique).sort();
  };

  const updateItem = (index: number, field: keyof CatalogItem, value: any) => {
    const newItems = [...editedItems];
    
    // Update the field
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-calculate delay if text changes for message type
    if (field === 'text_preview' && newItems[index].type === 'message') {
        const text = value as string;
        const words = text.trim().split(/\s+/).length;
        // 500ms per word, min 2000ms
        newItems[index].delay_before_send_ms = Math.max(2000, words * 500);
    }
    
    // Auto-update mime_type when type changes
    if (field === 'type') {
        const type = value as string;
        if (type === 'message') newItems[index].mime_type = 'text/plain';
        else if (type === 'audio') newItems[index].mime_type = 'audio/ogg';
        else if (type === 'sticker') newItems[index].mime_type = 'image/webp';
        else if (type === 'document') newItems[index].mime_type = 'application/pdf';
        else if (type === 'media') newItems[index].mime_type = 'image/jpeg';
    }
    
    setEditedItems(newItems);
  };

  const deleteItem = (index: number) => {
    const newItems = [...editedItems];
    newItems.splice(index, 1);
    setEditedItems(newItems);
  };

  const addItem = () => {
    setEditedItems([
        ...editedItems, 
        { 
            item_id: crypto.randomUUID(), 
            type: 'message', 
            mime_type: 'text/plain', 
            delay_before_send_ms: 2000, 
            text_preview: 'Nova mensagem',
            caption: '',
            ref_name: ''
        }
    ]);
  };
  
  const getItemIcon = (type: string) => {
    switch (type) {
        case 'audio': return <MicrophoneIcon className="h-4 w-4 mr-1 inline" />;
        case 'media': return <PhotoIcon className="h-4 w-4 mr-1 inline" />;
        case 'document': return <DocumentTextIcon className="h-4 w-4 mr-1 inline" />;
        case 'sticker': return <FaceSmileIcon className="h-4 w-4 mr-1 inline" />;
        default: return <ChatBubbleLeftIcon className="h-4 w-4 mr-1 inline" />;
    }
  };

  const createBlock = async () => {
    if (!newBlockName.trim()) {
        toast.error('Nome do comando é obrigatório');
        return;
    }
    
    // Normalize name (lowercase, no spaces)
    const normalizedName = newBlockName.trim().replace(/\s+/g, '_').toLowerCase();
    
    if (blocks[normalizedName]) {
        toast.error('Já existe um comando com este nome');
        return;
    }

    try {
        const newBlocks = { ...blocks };
        newBlocks[normalizedName] = {
            funnel_id: crypto.randomUUID(),
            description: newBlockDesc || 'Novo comando',
            items: [],
            fase: newBlockFase || undefined,
        };
        await LeadsAPI.saveCatalog(newBlocks);
        setBlocks(newBlocks);
        setShowNewBlockModal(false);
        setNewBlockName('');
        setNewBlockDesc('');
        setNewBlockFase('');
        toast.success(`Comando ${normalizedName} criado!`);
        // Start editing immediately
        startEdit(normalizedName, [], newBlockFase);
    } catch (error) {
        toast.error('Erro ao criar comando');
    }
  };

  const deleteBlock = async (blockName: string) => {
      if (!confirm(`Tem certeza que deseja excluir o comando "${blockName}"?`)) return;
      
      try {
        const newBlocks = { ...blocks };
        delete newBlocks[blockName];
        await LeadsAPI.saveCatalog(newBlocks);
        setBlocks(newBlocks);
        toast.success(`Comando ${blockName} excluído!`);
      } catch (error) {
        toast.error('Erro ao excluir comando');
      }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Catálogo de Administração (ZV)</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Gerencie as sequências de mensagens e mídias usadas pelos comandos de admin.
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
             <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="Buscar comando..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
             </div>

             {/* Fase filter */}
             <select
               value={faseFilter}
               onChange={e => setFaseFilter(e.target.value)}
               className="py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
             >
               <option value="">Todas as fases</option>
               {FASES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
               <option value="__none__">Sem fase</option>
             </select>

            <button
              onClick={() => setShowNewBlockModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
            >
              + Novo Comando
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {Object.entries(blocks)
            .filter(([name, block]) => {
                const search = searchTerm.toLowerCase();
                const matchesName = name.toLowerCase().includes(search);
                const matchesDesc = block.description.toLowerCase().includes(search);
                const matchesItem = block.items.some(item => 
                     (item.text_preview && item.text_preview.toLowerCase().includes(search)) ||
                     (item.caption && item.caption.toLowerCase().includes(search)) ||
                     (item.ref_name && item.ref_name.toLowerCase().includes(search))
                );
                const matchesFase = !faseFilter
                    ? true
                    : faseFilter === '__none__'
                        ? !block.fase
                        : block.fase === faseFilter;
                return (matchesName || matchesDesc || matchesItem) && matchesFase;
            })
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([blockName, block]) => (
          <div key={blockName} className="bg-white dark:bg-gray-800 shadow sm:rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-start bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Comando: <span className='font-mono bg-blue-100 text-blue-800 px-2 py-0.5 rounded'>{blockName}</span>
                  </h3>
                  {/* Fase badge / selector */}
                  {editingBlock === blockName ? (
                    <select
                      value={editedFase}
                      onChange={e => setEditedFase(e.target.value)}
                      className="text-xs border border-gray-300 dark:border-gray-600 rounded-full px-2 py-0.5 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="">Sem fase</option>
                      {FASES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                  ) : (
                    <FaseBadge faseId={block.fase} />
                  )}
                </div>
                <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
                  {block.description}
                </p>
              </div>
              <div>
                {editingBlock === blockName ? (
                   <div className="flex space-x-2">
                     <button
                        onClick={() => saveEdit(blockName)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" /> Salvar
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <XMarkIcon className="h-4 w-4 mr-1" /> Cancelar
                      </button>
                   </div>
                ) : (
                  <div className="flex space-x-2">
                    <button
                        onClick={() => startEdit(blockName, block.items, block.fase)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <PencilSquareIcon className="h-4 w-4 mr-1" /> Editar
                    </button>
                    <button
                        onClick={() => deleteBlock(blockName)}
                        className="inline-flex items-center px-3 py-2 border border-red-300 dark:border-red-900 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="border-t border-gray-200 dark:border-gray-700">
              <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700">
                {(editingBlock === blockName ? editedItems : block.items).map((item, index) => (
                  <li key={item.item_id || index} className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0 mr-4">
                        <div className="flex items-center mb-2 flex-wrap gap-2">
                          <span className={`px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full 
                            ${item.type === 'message' ? 'bg-blue-100 text-blue-800' : 
                              item.type === 'audio' ? 'bg-purple-100 text-purple-800' : 
                              (item.type === 'media' || item.type === 'sticker') ? 'bg-green-100 text-green-800' : 
                              item.type === 'document' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'}`}>
                            {getItemIcon(item.type)}
                            {item.type.toUpperCase()}
                          </span>
                          
                          {editingBlock === blockName ? (
                            <>
                            <div className="flex items-center space-x-2">
                                <label className="text-xs text-gray-500 whitespace-nowrap">Delay (s):</label>
                                <input 
                                    type="number"
                                    step="0.1"
                                    value={item.delay_before_send_ms / 1000}
                                    onChange={(e) => updateItem(index, 'delay_before_send_ms', parseFloat(e.target.value) * 1000)}
                                    className="block w-16 text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                            <div className="flex items-center space-x-2">
                                <select 
                                    value={item.type}
                                    onChange={(e) => updateItem(index, 'type', e.target.value)}
                                    className="block w-32 text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                    <option value="message">Mensagem</option>
                                    <option value="media">Mídia (Img/Vid)</option>
                                    <option value="audio">Áudio</option>
                                    <option value="document">PDF</option>
                                    <option value="sticker">Figurinha</option>
                                </select>
                            </div>
                            
                            <div className="flex-grow max-w-xs">
                                <select
                                    className="block w-full text-xs border-gray-300 rounded-md shadow-sm bg-yellow-50 text-gray-700"
                                    onChange={(e) => {
                                        if(!e.target.value) return;
                                        if(item.type === 'message') {
                                           updateItem(index, 'text_preview', e.target.value);
                                        } else {
                                            updateItem(index, 'ref_name', e.target.value);
                                        }
                                        e.target.value = ''; // Reset select
                                    }}
                                >
                                    <option value="">🔍 Buscar/Reaproveitar...</option>
                                    {getResources(item.type).map((res, i) => (
                                        <option key={i} value={res}>{res.substring(0, 40)}{res.length > 40 ? '...' : ''}</option>
                                    ))}
                                </select>
                            </div>
                            </>
                          ) : (
                            <span className="ml-2 text-xs text-gray-500">
                                Delay: {item.delay_before_send_ms / 1000}s
                            </span>
                          )}
                        </div>

                        {editingBlock === blockName ? (
                            <div className="space-y-2 mt-2">
                                {item.type === 'message' && (
                                    <textarea
                                        value={item.text_preview || ''}
                                        onChange={(e) => updateItem(index, 'text_preview', e.target.value)}
                                        rows={3}
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        placeholder="Texto da mensagem..."
                                    />
                                )}
                                {item.type !== 'message' && (
                                    <div className='grid grid-cols-1 gap-2 p-2 bg-gray-50 rounded'>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 block">Identificador do Arquivo (ref_name):</label>
                                            <input
                                                type="text"
                                                value={item.ref_name || ''}
                                                onChange={(e) => updateItem(index, 'ref_name', e.target.value)}
                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                placeholder={item.type === 'audio' ? 'Ex: Audio1' : 'Ex: imagem.jpg'}
                                            />
                                        </div>
                                        
                                        {(item.type === 'media' || item.type === 'document') && (
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 block">Legenda (Caption):</label>
                                            <input
                                                type="text"
                                                value={item.caption || ''}
                                                onChange={(e) => updateItem(index, 'caption', e.target.value)}
                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                placeholder="Legenda da mídia..."
                                            />
                                        </div>
                                        )}

                                         <div>
                                            <label className="text-xs font-medium text-gray-500 block">Preview Interno (Nota para Admin):</label>
                                            <input
                                                type="text"
                                                value={item.text_preview || ''}
                                                onChange={(e) => updateItem(index, 'text_preview', e.target.value)}
                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                placeholder="Texto curto para identificar visualmente"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="mt-1">
                                <p className="text-sm text-gray-900 dark:text-gray-300 whitespace-pre-wrap">
                                    {(item.text_preview && item.text_preview.length > 0) ? item.text_preview : 
                                     (item.caption && item.caption.length > 0) ? `[${item.type}] ${item.caption}` : 
                                     item.ref_name || 'Sem conteúdo visual'}
                                </p>
                            </div>
                        )}
                      </div>
                      
                      {editingBlock === blockName && (
                          <div className="ml-3 flex flex-col gap-1 shrink-0">
                            <button
                              onClick={() => moveItem(index, 'up')}
                              disabled={index === 0}
                              className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-20 disabled:cursor-not-allowed"
                              title="Mover para cima"
                            >
                              <ArrowUpIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => moveItem(index, 'down')}
                              disabled={index === editedItems.length - 1}
                              className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-20 disabled:cursor-not-allowed"
                              title="Mover para baixo"
                            >
                              <ArrowDownIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => removeItemFromBlock(index)}
                              className="p-1 rounded text-red-400 hover:text-red-600"
                              title="Remover item"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {editingBlock === blockName && (
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 text-right sm:px-6">
                    <button
                        onClick={addItemToBlock}
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        + Adicionar Mensagem
                    </button>
                  </div>
              )}
            </div>
          </div>
        ))}

        {Object.keys(blocks).length === 0 && (
            <div className="text-center py-12">
                <p className="text-gray-500">Nenhum bloco de catálogo encontrado.</p>
            </div>
        )}
      </div>

      {/* Modal Criar Bloco */}
      {showNewBlockModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                      Novo Comando de Admin
                    </h3>
                    <div className="mt-4">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Nome do Comando (sem espaços)
                        </label>
                        <input
                          type="text"
                          value={newBlockName}
                          onChange={(e) => setNewBlockName(e.target.value)}
                          placeholder="Ex: enviar_brinde"
                          className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Descrição
                        </label>
                        <input
                          type="text"
                          value={newBlockDesc}
                          onChange={(e) => setNewBlockDesc(e.target.value)}
                          placeholder="Ex: Envia PDF de brinde"
                          className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Fase do Funil
                        </label>
                        <select
                          value={newBlockFase}
                          onChange={e => setNewBlockFase(e.target.value)}
                          className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                          <option value="">Sem fase</option>
                          {FASES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={createBlock}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Criar
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewBlockModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogEditor;
